import type { AccommodationCardTrackingData } from "@website/accommodation-ui/accommodation-card";
import { addEventToDataLayer } from "@website/tracking/tracking.gtm";
import {
  DATA_LAYER_EVENTS,
  USER_TRACKING_EVENTS
} from "@website/tracking/tracking.types";
import { addEventToTracking } from "@website/tracking/user";
import type { ParsedUrlQuery } from "querystring";
import type { VillaListingCardType } from "./index.types";

type VillaAccommodationCardTrackingData = AccommodationCardTrackingData &
  Pick<VillaListingCardType, "cityId" | "cityTitle" | "propertyId">;

type VillaAccommodationCardTrackingOptions = {
  isMap?: boolean;
};

type VillaCityPageVisitedTrackingData = {
  city: string;
  city_id: number;
  date_from: Date;
  date_to: Date;
  available_hotels_id: Array<number>;
  page: number;
  last_minute_loaded: number;
  count_of_last_minute_cards: number;
  blue_box_loaded: number;
  count_of_blue_box_cards: number;
};

const getQueryValue = (value?: string | Array<string>) =>
  Array.isArray(value) ? value[0] : value;

const getTrackingDate = (value?: string | Array<string>) => {
  const date = getQueryValue(value);
  if (!date) return undefined;

  const trackingDate = new Date(date);

  return Number.isNaN(trackingDate.getTime()) ? undefined : trackingDate;
};

const getTrackingPrice = ({
  pricing,
  reservationInfo
}: Pick<VillaAccommodationCardTrackingData, "pricing" | "reservationInfo">) => {
  const shouldUseTotalPrice =
    reservationInfo?.guestsCount > 0 && reservationInfo?.nightCount > 0;

  return {
    price: shouldUseTotalPrice ? pricing.totalPrice : pricing.price,
    priceOff: shouldUseTotalPrice ? pricing.totalPriceOff : pricing.priceOff
  };
};

const getDiscountPercentage = (price: number, priceOff?: number) => {
  if (!price || !priceOff || price <= priceOff) return 0;

  return Math.ceil(((price - priceOff) * 100) / price);
};

const getDiscountAmount = (price: number, priceOff?: number) =>
  priceOff && price > priceOff ? price - priceOff : 0;

export const handleSearchInitiated = (data) => {
  addEventToTracking(USER_TRACKING_EVENTS.GENERAL_SEARCH, {
    product: "villa",
    ...data
  });
};

export const handleEventMapSearchClicked = (data) => {
  addEventToTracking(USER_TRACKING_EVENTS.MAP_SEARCH_CLICKED, {
    product: "villa",
    ...data
  });
};

export const handleCategoryBadgeFilterClicked = (data) => {
  addEventToTracking(USER_TRACKING_EVENTS.BADGE_FILTER_CLICKED, {
    product: "villa",
    ...data
  });
};

export const handleVillaCityPageVisited = (
  data: VillaCityPageVisitedTrackingData
) => {
  addEventToTracking(USER_TRACKING_EVENTS.PAGE_VISIT_CITY, {
    product: "villa",
    ...data
  });
};

export const handleVillaAccommodationCardTrackingEvents = (
  data: VillaAccommodationCardTrackingData,
  query: ParsedUrlQuery,
  options?: VillaAccommodationCardTrackingOptions
) => {
  const { price, priceOff } = getTrackingPrice(data);
  const ranking = data.index ?? 0;
  const city = data.cityTitle || "";
  const cityId = data.cityId || 0;

  if (data.trackingEvent === "view") {
    addEventToDataLayer(DATA_LAYER_EVENTS.VIEW_ITEM_LIST, {
      items: [
        {
          item_name: data.title,
          item_id: data.id,
          price,
          item_brand: "IRR",
          item_category: city,
          item_category2: "villa",
          item_list_name: `هتل های ${city}`,
          item_list_id: cityId,
          index: ranking
        }
      ]
    });

    addEventToTracking(USER_TRACKING_EVENTS.HOTEL_CARD_VIEWED, {
      position: ranking,
      accommodation_id: data.id,
      date_from: getTrackingDate(query.date_from),
      date_to: getTrackingDate(query.date_to),
      city,
      city_id: cityId,
      hotel: data.title,
      hotel_id: data.id,
      price,
      price_off: priceOff || 0,
      discount_percent: getDiscountPercentage(price, priceOff),
      is_available: true,
      product: "villa"
    });

    return;
  }

  addEventToDataLayer(DATA_LAYER_EVENTS.SELECT_ITEM, {
    items: [
      {
        item_name: data.title,
        item_id: data.id,
        price,
        discount: getDiscountAmount(price, priceOff),
        item_brand: "IRR",
        item_category: city,
        item_category2: "villa",
        item_list_name: `هتل های ${city}`,
        item_list_id: cityId,
        ranking
      }
    ]
  });

  addEventToTracking(USER_TRACKING_EVENTS.HOTEL_RESERVE_BUTTON_CLICKED, {
    position: ranking,
    date_from: getTrackingDate(query.date_from),
    date_to: getTrackingDate(query.date_to),
    city,
    city_id: cityId,
    hotel: data.title,
    hotel_id: data.id,
    price,
    price_off: priceOff || 0,
    discount_percent: getDiscountPercentage(price, priceOff),
    is_available: true,
    product: "villa"
  });

  if (!options?.isMap) return;

  addEventToTracking(USER_TRACKING_EVENTS.MAP_LISTING_CARD_CLICKED, {
    accommodation_id: data.id,
    click_area: data.clickArea,
    position: ranking,
    product: "villa"
  });
};
